import React from 'react';
import { resolveGameStoreHook } from '@core/gameengine/state/activeGameStore';
import { formatAmount, formatMoney, shouldUseCurrencySymbol } from '@core/i18n';
import {
  walletBalanceStyles,
  type WalletBalanceVariant,
} from './WalletBalance.styles';

const WALLET_BALANCE_FONT = "'Roboto', sans-serif";

export interface WalletBalanceProps {
  variant: WalletBalanceVariant;
  containerStyle?: React.CSSProperties;
  amountStyle?: React.CSSProperties;
  currencyStyle?: React.CSSProperties;
}

/**
 * Live wallet balance display, shared by the header and all modals
 * (rendered just before each modal's close button).
 *
 * Subscribes directly to the active game's Zustand store, so it re-renders
 * on every setBalance() call — bet placed, cash out, server balance push —
 * with no manual refresh wiring needed by callers.
 */
export const WalletBalance: React.FC<WalletBalanceProps> = ({
  variant,
  containerStyle,
  amountStyle,
  currencyStyle,
}) => {
  const activeController = (
    window as any
  ).playgrid?.getCurrentGameController?.();
  const useGameStore = resolveGameStoreHook(activeController);
  const { balance, currency } = useGameStore((s: any) => ({
    balance: s.balance,
    currency: s.currency,
  }));

  const amount = currency === 'CHIP' ? balance / 100 : balance;
  const useSymbol = shouldUseCurrencySymbol();
  const formattedBalance = useSymbol
    ? formatMoney(amount, currency)
    : formatAmount(amount, currency);
  const variantStyles = walletBalanceStyles[variant];

  return (
    <div
      data-wallet-balance={variant}
      style={{
        ...variantStyles.container,
        ...containerStyle,
        fontFamily: WALLET_BALANCE_FONT,
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ display: 'block', flexShrink: 0 }}
      >
        <path
          d="M21 12V7H5a2 2 0 0 1 0-4h14v4"
          stroke="rgba(255, 255, 255, 0.75)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 5v14a2 2 0 0 0 2 2h16v-5"
          stroke="rgba(255, 255, 255, 0.75)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M18 12a2 2 0 0 0 0 4h4v-4Z"
          stroke="rgba(255, 255, 255, 0.75)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
      >
        <span
          style={{
            ...variantStyles.amount,
            ...amountStyle,
            fontFamily: WALLET_BALANCE_FONT,
          }}
        >
          {formattedBalance}
        </span>
        {!useSymbol && (
          <span
            style={{
              ...variantStyles.currency,
              ...currencyStyle,
              fontFamily: WALLET_BALANCE_FONT,
            }}
          >
            {currency}
          </span>
        )}
      </span>
    </div>
  );
};
